import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import WidgetPageShell from '@components/common/WidgetPageShell';
import WidgetNav from '@components/common/WidgetNav';
import FooterBrand from '@components/common/FooterBrand';
import { COMPARISON_PAGE_SIZE } from '@apis/comparison';
import {
  mapComparisonPagination,
  mapPeerConsensusRatingToCards,
  mapPeerMetricToCards,
} from '@apis/comparisonMappers';
import {
  getApiParamErrorNotificationMessage,
  shouldNotifyDataRequestError,
} from '@apis/client';
import mockComparison from '@data/mockComparison';
import mockComparisonMetrics from '@data/mockComparisonMetrics';
import { useComparisonPeers, type ComparisonApiTabKey } from '@hooks/useComparisonPeers';
import { notify } from '@utils/notify';
import { formatUpdateDate } from '@utils/format';
import type {
  PeerConsensusRatingApiRow,
  PeerMetricApiRow,
} from '@app-types/comparisonApi';
import type { ComparisonMetricKey } from '@app-types/comparisonMetric';
import ComparisonTabs from './components/ComparisonTabs';
import ComparisonCardGrid from './components/ComparisonCardGrid';
import ComparisonPagination from './components/ComparisonPagination';
import MetricComparisonCardList from './components/MetricComparisonCardList';
import './ComparisonPage.less';

// Tab key -> metric map key. `rating` is the special case (no metric mock).
const TAB_TO_METRIC: Record<ComparisonApiTabKey, ComparisonMetricKey | undefined> = {
  rating: undefined,
  revenue: 'salesRevenue',
  ordinaryProfit: 'ordinaryProfit',
  netIncome: 'netIncome',
};

const METRIC_FLOW_NODE_IDS: Partial<Record<ComparisonMetricKey, string>> = {
  salesRevenue: '3662:31604',
};

const METRIC_PAGINATION_NODE_ID = '3450:31948';
const METRIC_FOOTER_NODE_ID = '3490:23044';
const METRIC_FOOTER_MARK_NODE_ID = '3490:23045';

const INITIAL_PAGE = 1;
const DEFAULT_TAB: ComparisonApiTabKey = 'rating';

const isComparisonTabKey = (value: string | undefined): value is ComparisonApiTabKey =>
  value === 'rating' ||
  value === 'revenue' ||
  value === 'ordinaryProfit' ||
  value === 'netIncome';

/**
 * Comparison screen (route /comparison).
 *
 * Four tab variants share the same shell:
 *   - `rating` → ComparisonCardGrid (2-column rating cards)
 *   - `revenue` → Sales & Revenue metric list (horizontal cards)
 *   - `ordinaryProfit` → Ordinary Profit metric list
 *   - `netIncome` → Net Income metric list
 *
 * Figma refs: consensus 3184:8693, sales 3226:16321, ordinary 3338:20886,
 * net income 3338:23748.
 */
const ComparisonPage: React.FC = () => {
  const initialActive = mockComparison.tabs.find((t) => t.active)?.key;
  const [activeTabKey, setActiveTabKey] = useState<ComparisonApiTabKey>(
    isComparisonTabKey(initialActive) ? initialActive : DEFAULT_TAB,
  );
  const [currentPage, setCurrentPage] = useState(INITIAL_PAGE);
  const comparisonQuery = useComparisonPeers(
    activeTabKey,
    currentPage,
    COMPARISON_PAGE_SIZE,
  );

  // Switching metric tabs shows a different list, so paging restarts at 1.
  const handleTabClick = useCallback((key: string) => {
    if (!isComparisonTabKey(key)) {
      return;
    }

    setActiveTabKey(key);
    setCurrentPage(INITIAL_PAGE);
  }, []);

  useEffect(() => {
    if (comparisonQuery.isError && shouldNotifyDataRequestError(comparisonQuery.error)) {
      notify({
        level: 'error',
        message: getApiParamErrorNotificationMessage(comparisonQuery.error) ??
          '他社比較データの取得に失敗しました。',
      });
    }
  }, [comparisonQuery.error, comparisonQuery.isError]);

  // Derive tabs with `active` flipped to whichever key is currently selected.
  const tabs = useMemo(
    () => mockComparison.tabs.map((t) => ({ ...t, active: t.key === activeTabKey })),
    [activeTabKey],
  );

  const metricKey = TAB_TO_METRIC[activeTabKey];
  const metricData = metricKey ? mockComparisonMetrics[metricKey] : undefined;
  const peerRows = comparisonQuery.data?.peers;

  // Skeleton is shown when the selected tab/page has no response data yet.
  // A completed empty/error response has `data` defined or `isFetching` false,
  // so the skeleton never sticks after the request settles.
  const isComparisonPageLoading =
    comparisonQuery.isLoading ||
    (comparisonQuery.isFetching && !comparisonQuery.data);

  // Keep the last known total pages per tab so a pending page request does not
  // collapse pagination back to page 1 while the new page is still loading.
  const lastTotalPagesByTabRef = useRef<Record<ComparisonApiTabKey, number>>({
    rating: 1,
    revenue: 1,
    ordinaryProfit: 1,
    netIncome: 1,
  });

  const totalPagesFromApi =
    comparisonQuery.data?.total !== undefined
      ? mapComparisonPagination(comparisonQuery.data.total, COMPARISON_PAGE_SIZE)
      : undefined;

  useEffect(() => {
    if (totalPagesFromApi !== undefined) {
      lastTotalPagesByTabRef.current[activeTabKey] = totalPagesFromApi;
    }
  }, [activeTabKey, totalPagesFromApi]);

  const totalPages =
    totalPagesFromApi ??
    Math.max(currentPage, lastTotalPagesByTabRef.current[activeTabKey] ?? 1);
  const ratingCards = useMemo(
    () => activeTabKey === 'rating'
      ? mapPeerConsensusRatingToCards(
        peerRows as PeerConsensusRatingApiRow[] | undefined,
      )
      : [],
    [activeTabKey, peerRows],
  );
  const metricCards = useMemo(
    () => metricKey
      ? mapPeerMetricToCards(peerRows as PeerMetricApiRow[] | undefined, metricKey)
      : [],
    [metricKey, peerRows],
  );

  return (
    <WidgetPageShell className="comparison">
      <WidgetNav />
      <main className="comparison__body">
        <ComparisonTabs
          tabs={tabs}
          updatedAt={`${formatUpdateDate(comparisonQuery.data?.updatedTime)} 更新`}
          onTabClick={handleTabClick}
          nodeId="3203:14212"
        />

        {activeTabKey === 'rating' ? (
          <>
            <ComparisonCardGrid
              cards={ratingCards}
              nodeId="3184:8701"
              isLoading={isComparisonPageLoading}
              placeholderCount={COMPARISON_PAGE_SIZE}
            />
            <ComparisonPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              disabled={isComparisonPageLoading}
              nodeId="3450:31851"
            />
            <FooterBrand className="comparison__footer" />
          </>
        ) : metricData && metricKey ? (
          <div
            className="comparison__metric-flow"
            data-node-id={METRIC_FLOW_NODE_IDS[metricKey] ?? metricData.webNodeId}
          >
            <MetricComparisonCardList
              cards={metricCards}
              nodeId={metricData.webNodeId}
              isLoading={isComparisonPageLoading}
              placeholderCount={COMPARISON_PAGE_SIZE}
            />
            <ComparisonPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              disabled={isComparisonPageLoading}
              nodeId={METRIC_PAGINATION_NODE_ID}
            />
            <FooterBrand
              className="comparison__metric-footer"
              nodeId={METRIC_FOOTER_NODE_ID}
              markNodeId={METRIC_FOOTER_MARK_NODE_ID}
            />
          </div>
        ) : null}
      </main>
    </WidgetPageShell>
  );
};

export default ComparisonPage;
